"use client";

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { subscribeAppearanceSettings } from "@/services/appearance-settings";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TapInfoState {
  text: string;
  x: number;
  y: number;
  visible: boolean;
}

interface TapInfoContextValue {
  /** Show a tooltip near the tapped element */
  showTapInfo: (text: string, element: HTMLElement) => void;
  /** Duration in ms (from admin settings) */
  duration: number;
}

const TapInfoContext = createContext<TapInfoContextValue>({
  showTapInfo: () => {},
  duration: 2000,
});

export const useTapInfo = () => useContext(TapInfoContext);

/* ------------------------------------------------------------------ */
/*  Helper: detect mobile / touch device                              */
/* ------------------------------------------------------------------ */

function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function TapInfoProvider({ children }: { children: React.ReactNode }) {
  const [tooltip, setTooltip] = useState<TapInfoState>({
    text: "",
    x: 0,
    y: 0,
    visible: false,
  });
  const [duration, setDuration] = useState(2000);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const durationRef = useRef(2000);

  // Keep ref in sync for use in event listener
  useEffect(() => { durationRef.current = duration; }, [duration]);

  // Subscribe to admin-configured duration
  useEffect(() => {
    const unsub = subscribeAppearanceSettings((settings) => {
      if (settings.tapInfoDuration && settings.tapInfoDuration > 0) {
        setDuration(settings.tapInfoDuration);
      }
    });
    return unsub;
  }, []);

  const showTapInfo = useCallback(
    (text: string, element: HTMLElement) => {
      if (!isTouchDevice()) return;
      if (!text) return;

      // Clear existing timer
      if (timerRef.current) clearTimeout(timerRef.current);

      // Position above the element, clamped to viewport
      const rect = element.getBoundingClientRect();
      let x = rect.left + rect.width / 2;
      let y = rect.top - 8;

      // Clamp x so tooltip doesn't go off-screen
      x = Math.max(60, Math.min(x, window.innerWidth - 60));
      // If too close to top, show below instead
      if (y < 40) y = rect.bottom + 8;

      setTooltip({ text, x, y, visible: true });

      timerRef.current = setTimeout(() => {
        setTooltip((prev) => ({ ...prev, visible: false }));
      }, durationRef.current);
    },
    []
  );

  // Global event delegation: any element with data-tap-info will trigger
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleTouch = (e: TouchEvent) => {
      if (!isTouchDevice()) return;
      const target = e.target as HTMLElement;
      // Walk up the DOM to find the closest element with data-tap-info
      const el = target.closest("[data-tap-info]") as HTMLElement | null;
      if (el) {
        const text = el.getAttribute("data-tap-info");
        if (text) showTapInfo(text, el);
      }
    };

    document.addEventListener("touchend", handleTouch, { passive: true });
    return () => document.removeEventListener("touchend", handleTouch);
  }, [showTapInfo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <TapInfoContext.Provider value={{ showTapInfo, duration }}>
      {children}

      {/* Global floating tooltip */}
      {tooltip.visible && (
        <div
          className={cn(
            "fixed z-[9999] pointer-events-none",
            "animate-in fade-in-0 zoom-in-95 duration-150"
          )}
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div
            className={cn(
              "px-2.5 py-1.5 rounded-lg text-xs font-medium max-w-[200px] text-center whitespace-nowrap",
              "bg-popover text-popover-foreground border border-border shadow-lg",
              "backdrop-blur-sm"
            )}
          >
            {tooltip.text}
            {/* Arrow */}
            <div
              className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
              style={{
                borderLeft: "5px solid transparent",
                borderRight: "5px solid transparent",
                borderTop: "5px solid hsl(var(--border))",
              }}
            />
          </div>
        </div>
      )}
    </TapInfoContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Convenience wrapper component                                      */
/* ------------------------------------------------------------------ */

interface TapInfoButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  info: string;
  children: React.ReactNode;
}

/**
 * Wraps any button-like element. On mobile tap, shows the info tooltip
 * for the configured duration. Still fires onClick normally.
 */
export const TapInfoButton = React.forwardRef<HTMLButtonElement, TapInfoButtonProps>(
  ({ info, children, onClick, ...props }, ref) => {
    const { showTapInfo } = useTapInfo();
    const innerRef = useRef<HTMLButtonElement | null>(null);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const el = innerRef.current;
      if (el) showTapInfo(info, el);
      onClick?.(e);
    };

    return (
      <button
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        }}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);
TapInfoButton.displayName = "TapInfoButton";
